import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import { AnimatePresence, motion } from 'framer-motion';
import {
  buildDerivationViewModel,
  formatCalcMoney,
  formatPct,
  type DerivationStepId,
  type DerivationViewModel,
  type IncomeCalcStream,
  type IncomeCalculationTransactions,
} from '../../lib/incomeCalculationDerivation';
import { ApplicationSectionHeader } from '../application/applicationSectionChrome';
import { IncomeStreamTabBar } from './IncomeStreamTabBar';
import type { IncludedIncomeStreamView } from '../../lib/incomeStreamPresentation';


const innerPaperSx = {
  p: 1.25,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

function formatPayDate(iso: string | null | undefined): string {
  if (!iso?.trim()) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function AmountSeriesTable({
  values,
  stream,
  highlightIndexes,
  valueLabel = 'Amount',
  showMeta = true,
}: {
  values: number[];
  stream: IncomeCalcStream;
  highlightIndexes?: Set<number>;
  valueLabel?: string;
  /** When false (e.g. sorted quantile list), hide date/memo columns — order is not chronological. */
  showMeta?: boolean;
}) {
  if (values.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No amounts recorded for this step.
      </Typography>
    );
  }

  const slots = stream.paySlots ?? [];
  const headers = showMeta ? ['#', 'Credit date', 'Payor memo', valueLabel] : ['#', valueLabel];

  return (
    <TableContainer sx={{ ...innerPaperSx, p: 0, overflow: 'auto', maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((h, hi) => (
              <TableCell
                key={h}
                align={hi === headers.length - 1 ? 'right' : 'left'}
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontSize: '0.65rem',
                  bgcolor: (t) =>
                    t.palette.mode === 'light' ? '#F7F8FA' : alpha(t.palette.common.white, 0.04),
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {values.map((v, i) => {
            const hot = highlightIndexes?.has(i);
            const slot = slots[i];
            return (
              <TableRow
                key={`${i}-${v}`}
                sx={{
                  bgcolor: hot
                    ? (t) => alpha(t.palette.warning.main, t.palette.mode === 'light' ? 0.06 : 0.12)
                    : undefined,
                }}
              >
                <TableCell sx={{ color: 'text.secondary', fontWeight: 600, width: 48 }}>
                  {i + 1}
                </TableCell>
                {showMeta ? (
                  <>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatPayDate(slot?.depositDate)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 220 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8125rem', fontWeight: 500, wordBreak: 'break-word' }}
                      >
                        {slot?.payorRaw?.trim() || stream.payor || '—'}
                      </Typography>
                    </TableCell>
                  </>
                ) : null}
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: hot ? 800 : 700,
                    color: hot ? 'warning.dark' : 'text.primary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCalcMoney(v)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Key | value list inside white paper — same family as Income attempt KV / DE detail. */
function DetailKvPanel({
  title,
  rows,
}: {
  title?: string;
  rows: Array<{ label: string; value: string; emphasize?: boolean }>;
}) {
  if (rows.length === 0) return null;
  return (
    <Box sx={innerPaperSx}>
      {title ? (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontSize: '0.65rem',
            display: 'block',
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box component="dl" sx={{ m: 0 }}>
        {rows.map((row, i) => (
          <Box
            key={`${row.label}-${i}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 42%) 1fr' },
              columnGap: 1.5,
              rowGap: 0.25,
              py: 0.85,
              alignItems: 'baseline',
              borderTop: i === 0 ? undefined : '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              component="dt"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              {row.label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{
                m: 0,
                fontWeight: row.emphasize ? 700 : 500,
                fontSize: row.emphasize ? '0.9375rem' : '0.875rem',
                color: 'text.primary',
                lineHeight: 1.45,
                wordBreak: 'break-word',
                fontVariantNumeric: 'tabular-nums',
                textAlign: { sm: 'right' },
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Prose({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
        fontWeight: 500,
        opacity: 0.78,
        lineHeight: 1.45,
      }}
    >
      {children}
    </Typography>
  );
}

function ResultCallout({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={innerPaperSx}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 42%) 1fr' },
          columnGap: 1.5,
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            m: 0,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.2,
            textAlign: { sm: 'right' },
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function StepDetail({ stepId, stream }: { stepId: DerivationStepId; stream: IncomeCalcStream }) {
  const iqr = stream.iqrCalculation;
  const mag = stream.magnitudeCalculation;
  const norm = stream.normalization;

  switch (stepId) {
    case 'identified':
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`Recurring credits for ${stream.payor}${
              stream.recurrenceEvents != null ? ` (${stream.recurrenceEvents} events)` : ''
            }. These amounts are the starting series for income math.`}
          </Prose>
          <AmountSeriesTable values={iqr.rawPayAmounts} stream={stream} valueLabel="Pay amount" />
        </Stack>
      );

    case 'rolling_median': {
      const applied = Boolean(iqr.smoothing?.applied && iqr.smoothedValues?.length);
      if (!applied) {
        return (
          <Prose>
            {iqr.smoothing
              ? `Rolling median was not used (min length ${iqr.smoothing.minLength}, window ${iqr.smoothing.window}). Quantiles use the raw pay series.`
              : 'Rolling median was not used for this run. Quantiles use the raw pay series.'}
          </Prose>
        );
      }
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`Trailing rolling median (window ${
              iqr.smoothing?.window ?? 3
            }) only places Q1/Q3 fences. Pay amounts that feed income stay raw until spike adjust.`}
          </Prose>
          <DetailKvPanel
            title="Settings"
            rows={[{ label: 'Bounds source', value: iqr.boundsFrom ?? 'smoothed', emphasize: true }]}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.65rem',
            }}
          >
            Smoothed series (value)
          </Typography>
          <AmountSeriesTable
            values={iqr.smoothedValues ?? []}
            stream={stream}
            valueLabel="Smoothed"
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.65rem',
            }}
          >
            Raw series (unchanged for income)
          </Typography>
          <AmountSeriesTable values={iqr.rawPayAmounts} stream={stream} valueLabel="Raw" />
        </Stack>
      );
    }

    case 'quantiles': {
      if (iqr.q1 == null || iqr.q3 == null || iqr.iqr == null) {
        return <Prose>Sample too small for quartile fences (need more than 2 pays).</Prose>;
      }
      const source =
        iqr.boundsFrom === 'smoothed' && iqr.smoothedValues?.length
          ? [...iqr.smoothedValues].sort((a, b) => a - b)
          : [...iqr.rawPayAmounts].sort((a, b) => a - b);
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`Sorted ${
              iqr.boundsFrom === 'smoothed' ? 'smoothed' : 'raw'
            } series → Q1 at 25% of n−1, Q3 at 75% of n−1. IQR = Q3 − Q1.`}
          </Prose>
          <DetailKvPanel
            title="Quantiles"
            rows={[
              { label: 'Q1 (25%)', value: formatCalcMoney(iqr.q1), emphasize: true },
              { label: 'Q3 (75%)', value: formatCalcMoney(iqr.q3), emphasize: true },
              { label: 'IQR (Q3 − Q1)', value: formatCalcMoney(iqr.iqr), emphasize: true },
            ]}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.65rem',
            }}
          >
            Sorted values used for quantiles
          </Typography>
          <AmountSeriesTable values={source} stream={stream} valueLabel="Sorted amount" showMeta={false} />
        </Stack>
      );
    }

    case 'fence': {
      const t = iqr.threshold ?? 1.5;
      if (iqr.lowerBound == null || iqr.upperBound == null || iqr.q1 == null || iqr.q3 == null || iqr.iqr == null) {
        return <Prose>No IQR fence was computed for this stream.</Prose>;
      }
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`Allowed range = (Q1 − ${t}×IQR) … (Q3 + ${t}×IQR). Pays above the upper fence are high spikes next; lows stay.`}
          </Prose>
          <DetailKvPanel
            title="Fence"
            rows={[
              { label: 'Threshold', value: String(t) },
              { label: 'Q1', value: formatCalcMoney(iqr.q1) },
              { label: 'Q3', value: formatCalcMoney(iqr.q3) },
              { label: 'IQR', value: formatCalcMoney(iqr.iqr) },
              {
                label: 'Lower bound',
                value: `${formatCalcMoney(iqr.q1)} − ${t} × ${formatCalcMoney(iqr.iqr)} = ${formatCalcMoney(iqr.lowerBound)}`,
                emphasize: true,
              },
              {
                label: 'Upper bound',
                value: `${formatCalcMoney(iqr.q3)} + ${t} × ${formatCalcMoney(iqr.iqr)} = ${formatCalcMoney(iqr.upperBound)}`,
                emphasize: true,
              },
            ]}
          />
        </Stack>
      );
    }

    case 'adjusted': {
      const rows =
        stream.calculationValueTrace.length > 0
          ? stream.calculationValueTrace
          : iqr.rawPayAmounts.map((originalPayAmount, i) => ({
              originalPayAmount,
              valueUsedInCalculation: iqr.payAmountsUsed[i] ?? originalPayAmount,
              replaced: (iqr.payAmountsUsed[i] ?? originalPayAmount) !== originalPayAmount,
              adjustmentKind:
                (iqr.payAmountsUsed[i] ?? originalPayAmount) !== originalPayAmount
                  ? 'high_spike_adjusted'
                  : 'none',
            }));
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`High spikes are capped toward the median-in-band${
              iqr.medianInBand != null ? ` (${formatCalcMoney(iqr.medianInBand)})` : ''
            }. The adjusted series feeds mode / recency / mean.`}
          </Prose>
          <DetailKvPanel
            title="Spike summary"
            rows={[
              {
                label: 'Median in band',
                value: iqr.medianInBand != null ? formatCalcMoney(iqr.medianInBand) : '—',
              },
              {
                label: 'High spikes',
                value:
                  iqr.highSpikeAmounts.length > 0
                    ? iqr.highSpikeAmounts.map((a) => formatCalcMoney(a)).join(', ')
                    : 'None capped',
                emphasize: true,
              },
            ]}
          />
          <TableContainer sx={{ ...innerPaperSx, p: 0, overflow: 'auto', maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['#', 'Credit date', 'Original', 'Used', 'Adjustment'].map((h, hi) => (
                    <TableCell
                      key={h}
                      align={hi === 2 || hi === 3 ? 'right' : 'left'}
                      sx={{
                        fontWeight: 800,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.65rem',
                        bgcolor: (t) =>
                          t.palette.mode === 'light' ? '#F7F8FA' : alpha(t.palette.common.white, 0.04),
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => {
                  const slot = stream.paySlots?.[i];
                  return (
                    <TableRow
                      key={i}
                      sx={{
                        bgcolor: row.replaced
                          ? (t) =>
                              alpha(t.palette.warning.main, t.palette.mode === 'light' ? 0.06 : 0.12)
                          : undefined,
                      }}
                    >
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatPayDate(slot?.depositDate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatCalcMoney(row.originalPayAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
                      >
                        {formatCalcMoney(row.valueUsedInCalculation)}
                      </TableCell>
                      <TableCell>
                        {row.replaced ? (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={row.adjustmentKind.replace(/_/g, ' ')}
                            sx={{ height: 22, fontWeight: 700 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      );
    }

    case 'magnitude': {
      const method = mag.magnitudeMethod;
      const rows: Array<{ label: string; value: string; emphasize?: boolean }> = [
        { label: 'Mode value', value: formatCalcMoney(mag.modeValue) },
        { label: 'Mode ratio', value: `${formatPct(mag.modeRatio)} (need ≥ 60% for mode)` },
        { label: 'Series mean', value: formatCalcMoney(mag.seriesMean) },
      ];
      if (mag.recency?.evaluated) {
        rows.push(
          {
            label: 'Last 3 mean',
            value:
              mag.recency.lastThreeMean != null ? formatCalcMoney(mag.recency.lastThreeMean) : '—',
          },
          {
            label: 'Hike gate',
            value: mag.recency.hikeGatePassed ? 'Passed (last 3 > mean × 1.05)' : 'Not passed',
          },
          {
            label: 'Recent mode share',
            value:
              mag.recency.recentModeRatio != null
                ? `${formatPct(mag.recency.recentModeRatio)} (need ≥ 67% / 2 of last 3)`
                : '—',
          },
        );
      }
      rows.push({ label: 'Rule that won', value: mag.rulePath || method, emphasize: true });
      return (
        <Stack spacing={1.25}>
          <Prose>
            From the adjusted series we pick one per-period amount: mode (≥60%), recency (salary-hike
            gate), or series mean.
          </Prose>
          <DetailKvPanel title="Magnitude inputs" rows={rows} />
          <ResultCallout label={`Period amount (${method})`} value={formatCalcMoney(mag.magnitude, 0)} />
        </Stack>
      );
    }

    case 'normalize':
      return (
        <Stack spacing={1.25}>
          <Prose>
            {`Frequency scales the period amount to a monthly figure${
              norm.frequency ? ` (${norm.frequency.replace(/_/g, ' ').toLowerCase()})` : ''
            }.`}
          </Prose>
          <DetailKvPanel
            title="Normalization"
            rows={[
              { label: 'Frequency', value: norm.frequency.replace(/_/g, ' '), emphasize: true },
              { label: 'Basis amount', value: formatCalcMoney(norm.basisAmount) },
              {
                label: 'Monthly factor',
                value: Number.isInteger(norm.monthlyFactor)
                  ? String(norm.monthlyFactor)
                  : norm.monthlyFactor.toFixed(2),
                emphasize: true,
              },
              { label: 'Formula', value: norm.formula || '—' },
            ]}
          />
          <ResultCallout label="Normalized monthly income" value={formatCalcMoney(norm.monthlyAmount, 0)} />
        </Stack>
      );

    default:
      return null;
  }
}

function StepTile({
  step,
  index,
  selected,
  onSelect,
}: {
  step: DerivationViewModel['steps'][number];
  index: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const theme = useTheme();
  const muted = Boolean(step.skipped);
  const stepNo = String(index + 1).padStart(2, '0');
  const accent = theme.palette.primary.main;

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`Step ${index + 1}: ${step.label}. ${step.shortResult}`}
      sx={{
        appearance: 'none',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        flex: 1,
        minWidth: { xs: '100%', sm: 0 },
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: selected ? alpha(accent, 0.55) : 'divider',
        bgcolor: selected
          ? alpha(accent, theme.palette.mode === 'light' ? 0.08 : 0.18)
          : 'background.paper',
        boxShadow: selected ? `inset 3px 0 0 ${accent}` : 'none',
        opacity: muted && !selected ? 0.72 : 1,
        transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
        '&:hover': {
          borderColor: alpha(accent, 0.4),
          bgcolor: alpha(accent, theme.palette.mode === 'light' ? 0.05 : 0.12),
        },
        '&:focus-visible': {
          outline: `2px solid ${accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: selected ? accent : 'text.secondary',
          fontSize: '0.62rem',
          display: 'block',
        }}
      >
        {stepNo} · {step.label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          mt: 0.45,
          lineHeight: 1.3,
          wordBreak: 'break-word',
          color: muted ? 'text.secondary' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {step.shortResult}
      </Typography>
      {step.skipped ? (
        <Chip
          size="small"
          label="Skipped"
          variant="outlined"
          sx={{ mt: 0.75, height: 22, fontWeight: 700 }}
        />
      ) : null}
    </Box>
  );
}

export function IncomeCalculationDerivationPanel({
  audit,
  primaryPayor,
  includedStreams = [],
}: {
  audit: IncomeCalculationTransactions | null;
  primaryPayor?: string | null;
  includedStreams?: IncludedIncomeStreamView[];
}) {
  const theme = useTheme();
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const defaultStreamKey = useMemo(() => {
    if (includedStreams.length === 0) return '';
    const primary = includedStreams.find((s) => s.isPrimary) ?? includedStreams[0];
    return primary?.payorKey ?? '';
  }, [includedStreams]);
  const [selectedStreamKey, setSelectedStreamKey] = useState(defaultStreamKey);
  const activeStream = useMemo(() => {
    if (includedStreams.length === 0) return null;
    return includedStreams.find((s) => s.payorKey === selectedStreamKey) ?? includedStreams[0] ?? null;
  }, [includedStreams, selectedStreamKey]);
  const selectedPayor = activeStream?.payor ?? primaryPayor ?? null;
  const vm = useMemo(() => buildDerivationViewModel(audit, selectedPayor), [audit, selectedPayor]);
  const [selectedId, setSelectedId] = useState<DerivationStepId | null>(null);

  useEffect(() => {
    setSelectedStreamKey(defaultStreamKey);
    setSelectedId(null);
  }, [defaultStreamKey, audit]);

  if (!vm) return null;

  const activeId =
    selectedId && vm.steps.some((s) => s.id === selectedId)
      ? selectedId
      : (vm.steps.find((s) => !s.skipped)?.id ?? vm.steps[0]?.id ?? null);
  const activeStep = vm.steps.find((s) => s.id === activeId) ?? null;
  const activeIndex = activeStep ? vm.steps.findIndex((s) => s.id === activeStep.id) : -1;
  const activeAccent = activeStream?.accent.main ?? theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      data-testid="income-calculation-derivation"
      sx={{ p: 1.75, borderRadius: 2, minWidth: 0 }}
    >
      <IncomeStreamTabBar
        streams={includedStreams}
        selectedPayorKey={activeStream?.payorKey ?? selectedStreamKey}
        onSelect={(key) => {
          setSelectedStreamKey(key);
          setSelectedId(null);
        }}
      />
      <ApplicationSectionHeader
        title="How monthly income was derived"
        subtitle={`${vm.payor} → ${formatCalcMoney(vm.monthlyAmount, 0)} / month. Select a step to see each key and value.`}
        icon={<FunctionsRoundedIcon fontSize="small" />}
        mb={1.5}
        endAdornment={
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            {vm.stream.reconstructed ? (
              <Chip
                size="small"
                variant="outlined"
                label="Rebuilt from audit"
                sx={{ height: 22, fontWeight: 700 }}
              />
            ) : null}
            <Chip
              size="small"
              variant="outlined"
              label={formatCalcMoney(vm.monthlyAmount, 0)}
              sx={{
                height: 22,
                fontWeight: 800,
                borderColor: alpha(activeAccent, 0.45),
                color: activeAccent,
              }}
            />
          </Stack>
        }
      />

      <Box
        sx={{
          ...innerPaperSx,
          mb: 1.25,
          position: 'sticky',
          top: {
            xs: 'calc(var(--admin-topbar-height, 0px) + 3.75rem)',
            md: 'calc(var(--admin-topbar-height, 52px) + 4.5rem)',
          },
          zIndex: 4,
          bgcolor: 'background.paper',
          // Soft indigo → sky wash — one quiet gradient behind clean paper tiles
          backgroundImage: (t) =>
            t.palette.mode === 'light'
              ? 'linear-gradient(90deg, #EEF2FF 0%, #EFF6FF 50%, #F0F9FF 100%)'
              : `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.12)} 0%, ${alpha(t.palette.info.main, 0.1)} 100%)`,
          boxShadow: (t) => `0 8px 16px -12px ${alpha(t.palette.common.black, 0.28)}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          role="list"
          aria-label="Income calculation steps"
          sx={{ width: '100%' }}
        >
          {vm.steps.map((step, idx) => (
            <Box key={step.id} role="listitem" sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              <StepTile
                step={step}
                index={idx}
                selected={step.id === activeId}
                onSelect={() => setSelectedId(step.id)}
              />
            </Box>
          ))}
        </Stack>
      </Box>

      {activeStep ? (
        <Box
          sx={{
            ...innerPaperSx,
            borderColor: alpha(activeAccent, 0.35),
            boxShadow: `inset 3px 0 0 ${activeAccent}`,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: activeAccent,
                  fontSize: '0.62rem',
                }}
              >
                {activeIndex >= 0 ? String(activeIndex + 1).padStart(2, '0') : '—'}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                {activeStep.label}
              </Typography>
              {activeStep.skipped ? (
                <Chip size="small" label="Skipped" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
              ) : null}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                columnGap: 1,
                alignItems: 'baseline',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Step result
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: activeAccent }}
              >
                {activeStep.shortResult}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 1.25 }} />
          {activeStep.skipped && activeStep.skipReason ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              {activeStep.skipReason}
            </Typography>
          ) : null}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep.id}
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <StepDetail stepId={activeStep.id} stream={vm.stream} />
            </motion.div>
          </AnimatePresence>
        </Box>
      ) : null}
    </Paper>
  );
}
